import localforage from 'localforage';
import { graphService } from '../services/graph.service';

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 1 day invalidation time

interface CachePhoto {
    photo?: string;
    timeCached: number;
}

const photoStore = localforage.createInstance({ name: 'photos', storeName: 'users' });
const groupPhotoStore = localforage.createInstance({ name: 'photos', storeName: 'groups' });

const graphAPI = new graphService();

// Remembers a miss as well as a hit: a user with no picture 404s from Graph, and without
// catching that, the miss was never cached - so the avatar was re-requested (and re-thrown
// uncaught) on every render/remount instead of caching the "no photo" result once. #34542
const pendingUserPhoto: { [id: string]: Promise<string> } = {};

const getNewPhoto = (idOrUpn: string): Promise<string> => {
    pendingUserPhoto[idOrUpn] ??= graphAPI
        .getUserPhoto(idOrUpn)
        .catch(() => "")
        .then(async src => {
            await photoStore.setItem<CachePhoto>(idOrUpn, { photo: src ?? "", timeCached: Date.now() });
            delete pendingUserPhoto[idOrUpn];
            return src ?? "";
        });

    return pendingUserPhoto[idOrUpn];
};

export const setCachedPhoto = async (idOrUpn: string, data: string): Promise<void> => {
    await photoStore.setItem<CachePhoto>(idOrUpn, { photo: data, timeCached: Date.now() });
};

export const getCachedPhoto = async (idOrUpn: string): Promise<string | undefined> => {
    const photoObj = await photoStore.getItem<CachePhoto>(idOrUpn);
    if (photoObj && CACHE_TTL_MS > Date.now() - photoObj.timeCached)
        return photoObj.photo;
    return getNewPhoto(idOrUpn);
};

// Team and Entra group photos, in their own store alongside the user ones.
// Remembers a miss as well as a hit: a group with no picture would otherwise be
// re-requested on every render of an Available To column. #34542
const pendingGroupPhoto: { [id: string]: Promise<string> } = {};

const getNewGroupPhoto = (id: string): Promise<string> => {
    pendingGroupPhoto[id] ??= graphAPI
        .getGroupPhoto(id)
        .catch(() => "")
        .then(async photo => {
            await groupPhotoStore.setItem<CachePhoto>(id, { photo: photo ?? "", timeCached: Date.now() });
            delete pendingGroupPhoto[id];
            return photo ?? "";
        });

    return pendingGroupPhoto[id];
};

export const getCachedGroupPhoto = async (id: string): Promise<string> => {
    if (!id) return "";

    const photoObj = await groupPhotoStore.getItem<CachePhoto>(id);
    if (photoObj && CACHE_TTL_MS > Date.now() - photoObj.timeCached)
        return photoObj.photo ?? "";

    return getNewGroupPhoto(id);
};
